import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { paymentAPI, taxConfigAPI } from '@matix/api-client';
import { paymentGatewayForCode, type PaymentGatewayCode, type PaymentType } from '@matix/types';
import { api, type ConvertBody, type ConvertPayment, type PaidSummary } from '../../lib/api';
import { fmtMoney, moneyPrecision, roundM } from '../../lib/currency';
import { useLang } from '../../lib/lang';
import type { SessionDeposit, SessionPayments } from '../../lib/types';
import { TapChargeDialog } from './TapChargeDialog';
import { useErrMsg } from '../../lib/errors';
import {
  buildConvertTender,
  priceAppointment,
  quickAmounts,
  tenderEpsilon,
  type TenderSplit,
} from '../../lib/tender';

/**
 * A hosted gateway's Inv_PaymentType.Code (-3 Tap, -4 UPayments, -5 Tabby,
 * -6 Tamara, -7 MyFatoorah) — the same table the till keys on. The server
 * lists a gateway's code only while its settings screen has it enabled.
 */

/**
 * Take payment for an appointment, then post it as a POS sale.
 *
 * This is the till's payment flow, not a lighter version of it. The receptionist
 * who converts an appointment is standing at the same counter, taking the same
 * money, and the receipt that comes out is an ordinary Code 51 POS receipt — so
 * it has to offer the same things: the tenant's real payment methods, several
 * of them on one receipt, cash over-tender with the change shown, and the
 * "Unpaid" sentinel for a customer who settles later. Before this dialog the
 * whole step was a `confirm()` that silently booked the lot on Cash, which made
 * every card payment taken at a salon reception land in the drawer's cash
 * total.
 *
 * Three rules are inherited from the cashier and are not negotiable, because
 * the server enforces them and rejects the sale otherwise:
 *
 *   - Σ splits must equal the total EXACTLY (at the tenant's currency
 *     precision — 3 decimals on KWD). Over-tender is not a bigger split: the
 *     last cash split is trimmed back to make the sum land, and the cash
 *     actually handed over travels separately as `cash_tendered`.
 *   - Money is rounded at the tenant precision at every step. Round-then-sum,
 *     never sum-then-round.
 *   - Unpaid (-1) and Credit (2) are exclusive with ordinary splits and need a
 *     customer on the appointment — you cannot owe money to nobody.
 *
 * VAT: the same tenant rate the till charges is applied on top of the service
 * prices. It is shown in the summary before anything is posted, so the number
 * on the screen is the number on the receipt.
 */

/**
 * One priced service on the appointment, as the SERVER holds it. Quantity is
 * carried explicitly: the calendar only ever books 1 of each, but a session
 * imported from the legacy TAPP tables can hold more, and pricing it at the
 * unit price would put a number on the screen that the receipt contradicts.
 */
export interface PaymentLine {
  name: string;
  qty: number;
  unit_price: number;
}

export interface PaymentDialogProps {
  sessionId: number;
  services: PaymentLine[];
  /** Cash customer on the appointment, if any. Gates the Unpaid CTA. */
  customerId: number | null;
  customerName: string | null;
  /**
   * The deposit an online booking already paid, straight off the session.
   * Null for a walk-in and for any tenant without the public portal.
   */
  deposit: SessionDeposit | null;
  /**
   * Deposits reception collected (migration 058). `available` is what the
   * invoice will settle from them; null on a tenant without the table.
   */
  payments?: SessionPayments | null;
  onClose: () => void;
  onPaid: (result: PaidSummary) => void;
}

export function PaymentDialog({
  sessionId,
  services,
  customerId,
  customerName,
  deposit,
  payments = null,
  onClose,
  onPaid,
}: PaymentDialogProps): JSX.Element {
  const { t } = useLang();
  const errMsg = useErrMsg();

  // Same query key the cashier uses, and the same long staleTime: ops change
  // payment methods in the back office, not between two appointments.
  const typesQ = useQuery({
    queryKey: ['payment-types'],
    queryFn: () => paymentAPI.listTypes(),
    staleTime: 10 * 60 * 1000,
  });
  const taxQ = useQuery({
    queryKey: ['tax-config'],
    queryFn: () => taxConfigAPI.get(),
    staleTime: 10 * 60 * 1000,
  });

  const [splits, setSplits] = useState<TenderSplit[]>([]);
  const [amountStr, setAmountStr] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** The gateway whose charge dialog is open, if any. */
  const [cardGateway, setCardGateway] = useState<PaymentGatewayCode | null>(null);

  // ── Money ──────────────────────────────────────────────────────────────
  // One smallest currency unit, capped at a cent, exactly as the till: a
  // hardcoded 0.01 would treat 0.008 KWD as nothing left to collect.
  const eps = useMemo(() => tenderEpsilon(moneyPrecision()), []);
  const step = useMemo(() => String(10 ** -moneyPrecision()), []);

  // Until the tax query lands we deliberately show 0% rather than a guess; the
  // Confirm button is disabled while it loads, so nothing can be posted at the
  // wrong rate.
  const taxPercent = taxQ.data?.sales_percentage ?? 0;
  const { subtotal, tax: taxValue, total } = useMemo(
    () => priceAppointment(services, taxPercent),
    [services, taxPercent],
  );

  // ── The deposit already paid online ────────────────────────────────────
  //
  // `min(available, total)` is the SAME expression the server applies in
  // `depositCreditFor`, and it has to stay that way: this dialog decides what
  // to ask the customer for, the server decides what to put on the receipt, and
  // Σ payments has to land on the total either way. The deposit itself is NOT
  // sent — the server adds that split from its own record of what Tap captured,
  // because a client that could name the figure could also get it wrong.
  const onlineCredit = useMemo(
    () => (deposit && deposit.available > 0 ? roundM(Math.min(deposit.available, total)) : 0),
    [deposit, total],
  );
  // Deposits reception collected (058) — paid and not yet on a receipt. The
  // server's `available` already holds online + reception together, capped at
  // its total; what is shown here as "at reception" is the part beyond the
  // online deposit, against THIS screen's total.
  const receptionCredit = useMemo(
    () => (payments ? roundM(Math.max(0, Math.min(payments.available, total) - onlineCredit)) : 0),
    [payments, total, onlineCredit],
  );
  const depositCredit = roundM(onlineCredit + receptionCredit);
  /**
   * What has to be collected NOW. Every tender figure on this screen is against
   * this and not against `total` — the invoice is still the total, but the
   * customer only owes the rest of it.
   */
  const due = roundM(total - depositCredit);

  const splitSum = roundM(splits.reduce((a, s) => a + s.amount, 0));
  const remaining = roundM(due - splitSum);
  const covered = remaining <= eps;
  const overTender = covered ? roundM(splitSum - due) : 0;
  // Change can only come out of a cash drawer. Over-tendering a card is a
  // typo, so the confirm stays blocked until it is corrected.
  const overTenderIsCash = splits.some((s) => s.isCash);
  const overTenderOk = overTender <= eps || overTenderIsCash;
  const entered = roundM(parseFloat(amountStr) || 0);

  // Nothing left to collect: an appointment where everything was comped, or —
  // now — one whose deposit already covers the whole invoice. Both still need a
  // method on the receipt at amount 0, same as the till's fully-discounted
  // sale, EXCEPT when a deposit covered it: there the deposit IS the tender and
  // adding a zero row on top would be noise on a receipt that is already paid.
  const isZeroTotal = due <= eps;
  const fullyCoveredByDeposit = depositCredit > 0 && due <= eps;

  const allTypes = typesQ.data?.payment_types ?? [];
  // Same three-way split as the till, minus the one that genuinely does not
  // apply: Multi (0) is a sentinel the server writes, and Credit (2) needs a
  // GL-account customer the appointments customer picker cannot produce (it
  // searches Inv_CashCustomer).
  //
  // Tap (-3) is NOT in this grid but is not missing either: it opens the card
  // dialog from its own button below, exactly as the till's grid routes -3 to
  // TapPaymentDialog instead of adding a split. A card is charged through a
  // gateway, so it cannot be a figure somebody types into a split.
  const methods = allTypes.filter((m) => m.code > 0 && m.code !== 2);
  const unpaidType = allTypes.find((m) => m.code === -1) ?? null;
  // One button per enabled gateway, in the order the server listed them.
  const gatewayTypes = allTypes.flatMap((m) => {
    const gw = paymentGatewayForCode(m.code);
    return gw ? [{ type: m, gateway: gw }] : [];
  });
  // A deposit is money already taken, and the legacy schema writes no payment
  // row for the Unpaid sentinel — so "unpaid" would quietly discard it. The
  // server refuses the combination too (DEPOSIT_ON_ACCOUNT_CONFLICT); hiding
  // the CTA is how the receptionist finds that out before typing anything.
  const canGoUnpaid = unpaidType != null && customerId != null && depositCredit <= 0;

  // Prefill the input with what is still owed after every add/remove. Keyed on
  // splits.length, not on `remaining`, so typing does not fight the effect.
  useEffect(() => {
    setAmountStr(fmtMoney(remaining > 0 ? remaining : 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splits.length, due]);

  function addSplit(type: PaymentType): void {
    if (saving) return;
    setNotice(null);
    if (fullyCoveredByDeposit) {
      setNotice(t({
        en: 'The deposit already covers this invoice — nothing more to collect.',
        ar: 'العربون يغطي هذه الفاتورة بالكامل — لا يوجد مبلغ إضافي للتحصيل.',
      }));
      return;
    }
    if (isZeroTotal) {
      // Replace rather than append: one method, recorded at zero.
      setSplits([{ code: type.code, name: type.name, amount: 0, isCash: type.is_cash }]);
      return;
    }
    if (splits.some((s) => s.code === type.code)) {
      setNotice(t({
        en: `${type.name} is already applied — remove it to change the amount.`,
        ar: `${type.name} مُطبَّقة بالفعل — أزلها لتغيير المبلغ.`,
      }));
      return;
    }
    if (entered <= 0) {
      setNotice(t({ en: 'Enter an amount first.', ar: 'أدخل المبلغ أولاً.' }));
      return;
    }
    let amount = entered;
    if (!type.is_cash && amount > remaining + eps) {
      amount = remaining;
      setNotice(t({
        en: `${type.name} clamped to the remaining ${fmtMoney(remaining)}.`,
        ar: `تم ضبط ${type.name} على المتبقي ${fmtMoney(remaining)}.`,
      }));
    }
    if (amount <= 0) {
      setNotice(t({ en: 'The invoice is already covered.', ar: 'الفاتورة مغطاة بالكامل.' }));
      return;
    }
    setSplits((prev) => [
      ...prev,
      { code: type.code, name: type.name, amount: roundM(amount), isCash: type.is_cash },
    ]);
  }

  function removeSplit(code: number): void {
    if (saving) return;
    setNotice(null);
    setSplits((prev) => prev.filter((s) => s.code !== code));
  }

  async function post(body: ConvertBody): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      const result = await api.convert(sessionId, body);
      onPaid(result);
    } catch (e) {
      // The sale did NOT post — the error stays in the dialog with the splits
      // intact so the receptionist can fix one figure and retry, rather than
      // rebuilding the whole tender.
      setError(errMsg(e));
      setSaving(false);
    }
  }

  function confirm(): void {
    if (saving || !canConfirm) return;
    // An invoice the deposit already covers is posted with NO splits at all:
    // the server adds the deposit tender itself and will not default the rest
    // onto Cash, so sending nothing is precisely how to ask for that.
    if (fullyCoveredByDeposit) {
      void post({ tax_percentage: taxPercent, complete: true });
      return;
    }
    // Σ splits is trimmed onto what is DUE and the over-tender travels as
    // `cash_tendered` — see `buildConvertTender` for why it is not the cash
    // rows' own sum.
    const { payments, cashTendered } = buildConvertTender(splits, due, eps);
    void post({
      payments: payments as ConvertPayment[],
      ...(cashTendered != null ? { cash_tendered: cashTendered } : {}),
      tax_percentage: taxPercent,
      complete: true,
    });
  }

  function confirmUnpaid(): void {
    if (saving || !unpaidType || customerId == null) return;
    const who = customerName || t({ en: 'this customer', ar: 'هذا العميل' });
    const ok = window.confirm(t({
      en: `Save ${fmtMoney(total)} unpaid against ${who}?`,
      ar: `حفظ ${fmtMoney(total)} كغير مدفوع على ${who}؟`,
    }));
    if (!ok) return;
    void post({
      payments: [{ type_code: unpaidType.code, amount: total }],
      tax_percentage: taxPercent,
      complete: true,
    });
  }

  const configLoading = typesQ.isLoading || taxQ.isLoading;
  // A failed tax lookup must NOT fall through to 0%. `taxQ.data` is undefined
  // either way, so without this the dialog would quietly price the invoice
  // ex-VAT and post it — under-charging the customer and under-declaring the
  // tax, with nothing on screen to say so.
  const configFailed = taxQ.isError || typesQ.isError;
  const canConfirm =
    !saving &&
    !configLoading &&
    !configFailed &&
    // A deposit-covered invoice has no splits to apply and is confirmable on
    // its own; every other invoice needs at least one method chosen.
    (fullyCoveredByDeposit || splits.length > 0) &&
    covered &&
    overTenderOk;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center overflow-hidden bg-black/50 sm:items-start sm:overflow-auto sm:p-6 md:p-8"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div
        className="appt-dialog appt-card flex h-full w-full max-w-3xl flex-col rounded-none border-0 sm:my-auto sm:h-auto sm:rounded-2xl sm:border"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-none items-start justify-between gap-2 border-b border-slate-200 px-4 py-3 sm:border-0 sm:px-6 sm:pb-0 sm:pt-6 dark:border-slate-700 dark:sm:border-0">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-bold text-slate-800 sm:text-xl dark:text-slate-100">
              {t({ en: 'Take payment', ar: 'استلام الدفع' })}
            </h2>
            <div className="truncate text-sm text-slate-500 dark:text-slate-400">
              {services.length}{' '}
              {services.length === 1
                ? t({ en: 'service', ar: 'خدمة' })
                : t({ en: 'services', ar: 'خدمات' })}
              {customerName ? ` · ${customerName}` : ''} · {fmtMoney(total)}
            </div>
          </div>
          <div className="flex flex-none items-center gap-2">
            {canGoUnpaid && (
              <button
                type="button"
                className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-semibold text-red-600 transition hover:bg-red-100 disabled:opacity-50 dark:border-red-900 dark:bg-red-950 dark:text-red-300"
                onClick={confirmUnpaid}
                disabled={saving || splits.length > 0}
                title={
                  splits.length > 0
                    ? t({ en: 'Remove the applied payments first', ar: 'أزل المدفوعات المطبقة أولاً' })
                    : t({ en: 'Save this invoice against the customer’s balance', ar: 'حفظ هذه الفاتورة على رصيد العميل' })
                }
              >
                {t({ en: 'Unpaid', ar: 'غير مدفوع' })}
              </button>
            )}
            <button
              className="-me-1 flex h-10 w-10 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50 dark:hover:bg-slate-800"
              onClick={onClose}
              disabled={saving}
              aria-label={t({ en: 'Close', ar: 'إغلاق' })}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="appt-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:overflow-y-visible sm:px-6">
          <div className="grid gap-5 sm:grid-cols-2">
            {/* LEFT — amount + methods */}
            <div>
              <label className="appt-label" htmlFor="appt-pay-amount">
                {t({ en: 'Amount to apply', ar: 'المبلغ المطبق' })}
              </label>
              <input
                id="appt-pay-amount"
                type="number"
                inputMode="decimal"
                min="0"
                step={step}
                className="appt-input text-lg font-semibold tabular-nums"
                value={amountStr}
                onChange={(e) => setAmountStr(e.target.value)}
                disabled={saving || covered || isZeroTotal}
                autoFocus
              />

              {!isZeroTotal && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {quickAmounts(remaining > 0 ? remaining : total).map((amt) => (
                    <button
                      key={amt}
                      type="button"
                      className="rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-200 disabled:opacity-40 dark:bg-slate-800 dark:text-slate-200"
                      onClick={() => setAmountStr(fmtMoney(amt))}
                      disabled={saving || covered}
                    >
                      {fmtMoney(amt)}
                    </button>
                  ))}
                </div>
              )}

              <div className="mt-4 mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {t({ en: 'Payment method', ar: 'طريقة الدفع' })}
              </div>
              {configLoading && (
                <div className="text-sm text-slate-500">{t({ en: 'Loading methods…', ar: 'جارٍ تحميل الطرق…' })}</div>
              )}
              {!configLoading && methods.length === 0 && (
                <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950 dark:text-amber-200">
                  {t({
                    en: 'No sales payment methods are configured for this subsidiary.',
                    ar: 'لا توجد طرق دفع للمبيعات مُعدَّة لهذه الشركة.',
                  })}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                {methods.map((type) => {
                  const used = splits.some((s) => s.code === type.code);
                  const blocked = isZeroTotal ? splits.length > 0 && !used : covered;
                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => addSplit(type)}
                      disabled={saving || used || blocked}
                      className={`flex min-h-[52px] flex-col items-center justify-center rounded-lg border px-3 py-2 text-sm font-semibold transition disabled:opacity-40 ${
                        used
                          ? 'border-brand bg-brand/10 text-brand'
                          : type.is_cash
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200'
                            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200'
                      }`}
                    >
                      <span className="truncate">{type.name}</span>
                      {type.name_ar && type.name_ar !== type.name && (
                        <span className="truncate text-xs font-normal opacity-70">{type.name_ar}</span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/*
                Card, through the tenant's own Tap gateway — the "online"
                method reception could not reach before, which is why a card
                taken at a salon desk either walked the customer to the till or
                got booked against whatever method was nearest.

                It sits outside the grid because it does not behave like the
                others: it charges the balance through a gateway and posts the
                sale itself, so there is nothing to add to the splits and
                nothing to combine it with. Shown only when the tenant actually
                has Tap configured — the -3 row is the same signal the till
                uses — and only while there is a balance to charge.
              */}
              {!isZeroTotal && gatewayTypes.map(({ type: gwType, gateway }) => (
                <button
                  key={gwType.id}
                  type="button"
                  onClick={() => setCardGateway(gateway.code)}
                  disabled={saving || splits.length > 0}
                  title={
                    splits.length > 0
                      ? t({
                          en: 'Remove the applied payments first — a card charge pays the whole balance.',
                          ar: 'أزل المدفوعات المطبقة أولاً — الدفع بالبطاقة يغطي كامل المتبقي.',
                        })
                      : undefined
                  }
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-3 text-sm font-semibold text-indigo-800 transition hover:bg-indigo-100 disabled:opacity-40 dark:border-indigo-900 dark:bg-indigo-950 dark:text-indigo-200"
                >
                  <CardIcon />
                  <span>
                    {gateway.code === 'tap'
                      ? t({ en: 'Pay by card', ar: 'الدفع بالبطاقة' })
                      : t({ en: `Pay with ${gateway.nameEn}`, ar: `الدفع عبر ${gateway.nameAr}` })}{' '}
                    · {fmtMoney(due)}
                  </span>
                </button>
              ))}
            </div>

            {/* RIGHT — summary, applied splits, balance */}
            <div>
              <div className="rounded-xl border border-slate-200 p-3 dark:border-slate-700">
                {/* What is being charged, line by line — the receptionist is
                    committing to these figures, so they belong on screen and
                    not only in the total. */}
                {services.map((s, i) => (
                  <div
                    key={`${s.name}-${i}`}
                    className="flex items-center justify-between gap-2 py-0.5 text-sm text-slate-700 dark:text-slate-200"
                  >
                    <span className="min-w-0 flex-1 truncate">
                      {s.qty > 1 ? `${s.qty} × ` : ''}
                      {s.name}
                    </span>
                    <span className="tabular-nums">{fmtMoney(roundM(s.qty * s.unit_price))}</span>
                  </div>
                ))}
                <div className="my-2 border-t border-slate-200 dark:border-slate-700" />
                <Row label={t({ en: 'Subtotal', ar: 'المجموع الفرعي' })} value={fmtMoney(subtotal)} />
                {taxValue > 0 && (
                  <Row
                    label={`${t({ en: 'VAT', ar: 'ضريبة القيمة المضافة' })} ${taxPercent}%`}
                    value={fmtMoney(taxValue)}
                  />
                )}
                <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-2 text-base font-bold text-slate-800 dark:border-slate-700 dark:text-slate-100">
                  <span>{t({ en: 'Total', ar: 'الإجمالي' })}</span>
                  <span className="tabular-nums">{fmtMoney(total)}</span>
                </div>

                {/* The deposit, and what it leaves. Shown as its own pair of
                    rows under the total rather than folded into it: the invoice
                    IS the total — the salon delivered the whole service and
                    declares the whole service — and only the money still to
                    collect has changed. */}
                {depositCredit > 0 && (
                  <>
                    {onlineCredit > 0 && (
                      <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-2 text-sm font-semibold text-emerald-700 dark:border-slate-700 dark:text-emerald-300">
                        <span>
                          {t({ en: 'Paid online (deposit)', ar: 'مدفوع أونلاين (عربون)' })}
                          {deposit?.public_ref ? ` · ${deposit.public_ref}` : ''}
                        </span>
                        <span className="tabular-nums">−{fmtMoney(onlineCredit)}</span>
                      </div>
                    )}
                    {receptionCredit > 0 && (
                      <div className={`${onlineCredit > 0 ? 'mt-1' : 'mt-2 border-t border-slate-200 pt-2 dark:border-slate-700'} flex items-center justify-between text-sm font-semibold text-emerald-700 dark:text-emerald-300`}>
                        <span>{t({ en: 'Deposits already paid', ar: 'عرابين مدفوعة مسبقًا' })}</span>
                        <span className="tabular-nums">−{fmtMoney(receptionCredit)}</span>
                      </div>
                    )}
                    <div className="mt-1 flex items-center justify-between text-base font-bold text-slate-800 dark:text-slate-100">
                      <span>{t({ en: 'Due now', ar: 'المستحق الآن' })}</span>
                      <span className="tabular-nums">{fmtMoney(due)}</span>
                    </div>
                  </>
                )}

                {/* A deposit that was taken and then spent on an earlier
                    receipt. Worth saying out loud: the customer will ask why
                    they are being charged the full amount. */}
                {deposit && deposit.paid > 0 && depositCredit <= 0 && (
                  <div className="mt-2 border-t border-slate-200 pt-2 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
                    {deposit.applied_receipt_id != null
                      ? t({
                          en: `A ${fmtMoney(deposit.paid)} deposit was already applied to invoice #${deposit.applied_receipt_id}.`,
                          ar: `تم استخدام عربون ${fmtMoney(deposit.paid)} على الفاتورة رقم ${deposit.applied_receipt_id}.`,
                        })
                      : t({
                          en: `A ${fmtMoney(deposit.paid)} deposit is recorded as “${deposit.status}” and cannot be settled here.`,
                          ar: `العربون ${fmtMoney(deposit.paid)} مسجَّل بحالة “${deposit.status}” ولا يمكن تسويته هنا.`,
                        })}
                  </div>
                )}
              </div>

              <div className="mt-3 rounded-xl border border-slate-200 p-3 dark:border-slate-700">
                <div className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <span>{t({ en: 'Applied', ar: 'المطبق' })}</span>
                  {splits.length > 0 && (
                    <button
                      type="button"
                      className="text-xs font-semibold normal-case text-red-600 hover:underline disabled:opacity-50"
                      onClick={() => setSplits([])}
                      disabled={saving}
                    >
                      {t({ en: 'Clear all', ar: 'مسح الكل' })}
                    </button>
                  )}
                </div>
                {splits.length === 0 ? (
                  <div className="text-sm text-slate-400">
                    {t({ en: 'No payments applied yet', ar: 'لم يتم تطبيق أي دفعات بعد' })}
                  </div>
                ) : (
                  splits.map((s) => (
                    <div key={s.code} className="flex items-center gap-2 py-1 text-sm">
                      <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-slate-200">{s.name}</span>
                      <span className="tabular-nums font-semibold text-slate-800 dark:text-slate-100">
                        {fmtMoney(s.amount)}
                      </span>
                      <button
                        type="button"
                        className="flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                        onClick={() => removeSplit(s.code)}
                        disabled={saving}
                        aria-label={t({ en: `Remove ${s.name}`, ar: `إزالة ${s.name}` })}
                      >
                        ✕
                      </button>
                    </div>
                  ))
                )}
              </div>

              <div className="mt-3">
                {!covered && (
                  <div className="flex items-center justify-between rounded-xl bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800 dark:bg-amber-950 dark:text-amber-200">
                    <span>{t({ en: 'Balance due', ar: 'المبلغ المتبقي' })}</span>
                    <span className="tabular-nums">{fmtMoney(remaining)}</span>
                  </div>
                )}
                {covered && overTender > eps && overTenderIsCash && (
                  <div className="flex items-center justify-between rounded-xl bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-800 dark:bg-blue-950 dark:text-blue-200">
                    <span>{t({ en: 'Change due', ar: 'الباقي' })}</span>
                    <span className="tabular-nums">{fmtMoney(overTender)}</span>
                  </div>
                )}
                {covered && overTender > eps && !overTenderIsCash && (
                  <div className="rounded-xl bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 dark:bg-red-950 dark:text-red-200">
                    {t({
                      en: 'A card payment can’t be over-tendered — no change can be given from it.',
                      ar: 'لا يمكن تجاوز مبلغ الدفع بالبطاقة — لا يمكن إعطاء باقٍ منها.',
                    })}
                  </div>
                )}
                {covered && overTender <= eps && (
                  <div className="rounded-xl bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700 dark:bg-emerald-950 dark:text-emerald-200">
                    {t({ en: 'Fully paid', ar: 'تم الدفع بالكامل' })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {configFailed && (
            <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700 dark:bg-red-950 dark:text-red-200">
              {t({
                en: 'Could not load the tax rate and payment methods, so this invoice cannot be priced. Close and try again.',
                ar: 'تعذّر تحميل نسبة الضريبة وطرق الدفع، لذا لا يمكن تسعير هذه الفاتورة. أغلق النافذة وحاول مرة أخرى.',
              })}
            </div>
          )}
          {notice && (
            <div className="mt-3 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              {notice}
            </div>
          )}
          {error && (
            <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700 dark:bg-red-950 dark:text-red-200">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="appt-dialog-footer flex flex-none gap-2 border-t border-slate-200 px-4 pt-3 sm:px-6 sm:pt-4 dark:border-slate-700">
          <button className="appt-btn-ghost flex-1 sm:flex-none" onClick={onClose} disabled={saving}>
            {t({ en: 'Cancel', ar: 'إلغاء' })}
          </button>
          <button className="appt-btn-primary flex-1 sm:ms-auto sm:flex-none" onClick={confirm} disabled={!canConfirm}>
            {saving
              ? t({ en: 'Posting…', ar: 'جارٍ الترحيل…' })
              : fullyCoveredByDeposit
                ? `${t({ en: 'Issue invoice', ar: 'إصدار الفاتورة' })} · ${fmtMoney(total)}`
                : `${t({ en: 'Confirm', ar: 'تأكيد' })} · ${fmtMoney(due)}`}
          </button>
        </div>
      </div>

      {cardGateway && (
        <TapChargeDialog
          gateway={cardGateway}
          sessionId={sessionId}
          taxPercent={taxPercent}
          total={total}
          depositCredit={depositCredit}
          onClose={() => setCardGateway(null)}
          onPaid={(r) => {
            setCardGateway(null);
            onPaid(r);
          }}
        />
      )}
    </div>
  );
}

function CardIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
    </svg>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-center justify-between py-0.5 text-sm text-slate-600 dark:text-slate-300">
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
